import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {
  ClickEventHandler,
  EventHandler,
  HandlerOf,
  KeyboardEventHandler,
  RendererOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {OrderInputBox} from '../../boxedInputs/orderInputBox';
import {getCellBackgroundColor, getCellIndexStyles} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

export interface NumberInputCellProps extends TableCellProps<number> {
  onIncrement?: ClickEventHandler;
  onDecrement?: ClickEventHandler;
  onBlur?: EventHandler<HTMLInputElement>;
  onChange?: HandlerOf<number | ''>;
  onKeyDown?: KeyboardEventHandler;
  maxSortNumber?: number;
  isInMultiSelectMode?: boolean;
  disableIncrement?: boolean;
  disableDecrement?: boolean;
}

/*
 * Styles.
 */

const StyledInputCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  align-items: center;
  height: 100%;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

/*
 * Component.
 */

export const NumberInputCell = (props: NumberInputCellProps): ReactElement => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    disableDecrement,
    disableIncrement,
    onIncrement,
    onDecrement,
    onBlur,
    onChange,
    onKeyDown,
    maxSortNumber,
    isInMultiSelectMode,
  } = props;

  const selectedInputStyles =
    isInMultiSelectMode && isSelected
      ? {
          backgroundColor: theme.alphas.white10,
          color: theme.alphas.white50,
          boxShadow: `inset 0 0 0 2px ${theme.alphas.white30}`,
          opacity: 1,
        }
      : undefined;

  const renderInput: RendererOf<number | undefined> = (value) => (
    <StyledInputCellContainerDiv
      $settings={settings}
      $isHovering={Boolean(isHovering)}
      $index={index}
      $isSelected={Boolean(isSelected)}
      $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      onClick={(e) => {
        // If not disabled.
        if (!isInMultiSelectMode) {
          e.preventDefault();
        }
      }}
    >
      <OrderInputBox
        boxStyles={selectedInputStyles}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
        onKeyDown={onKeyDown}
        min={1}
        max={maxSortNumber}
        size={VisualSizesEnum.SMALL}
        isDisabled={isInMultiSelectMode}
        isArrowsDisabled={isInMultiSelectMode}
        inputWidth={48}
        disableDownArrow={disableDecrement}
        disableUpArrow={disableIncrement}
        onDownArrow={onDecrement}
        onUpArrow={onIncrement}
      />
    </StyledInputCellContainerDiv>
  );

  return (
    <TableCell<number>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderCell={renderInput}
      renderEmptyState={renderInput}
    />
  );
};
